import { useMemo } from "react";

import type { NumberOperator } from "@skilitics/judy";
import { useFormatMessage } from "@skilitics/localization";
import { isReferencedValue, type ValueConfigFor } from "@skilitics/nautilus-core-designer-data";

import { ValueConfigSummaryText } from "../../../values/forms/ValueConfigSummaryText.tsx";
import type { NumberConditionFormProps, NumberConditionFormValues } from "../forms";
import { NumberConditionForm } from "../forms";
import { isTernaryOperator, operatorToEnglish } from "../helpers";
import { ConditionBuilderSummary } from "../ui/Summary";

export type NumericValueConfig = ValueConfigFor<"number">;

export type NumericConditionValues = {
  operator?: NumberOperator;
  value?: NumericValueConfig;
  value2?: NumericValueConfig;
};

export type NumericConditionEditorProps = Omit<NumberConditionFormProps, "initialValues" | "onSubmit"> & {
  disabled?: boolean;
  title?: string;
  offset?: number;
  values?: NumericConditionValues;
  onChange: (values: NumericConditionValues) => void;
};

const EmptyValues: Readonly<NumericConditionValues> = { operator: undefined, value: undefined, value2: undefined };

// the offset only applies to a static literal; a referenced value is already in stored units
const addOffset = (configuredValue: NumericValueConfig | undefined, offset: number, fallback: number) =>
  configuredValue === undefined
    ? fallback
    : isReferencedValue(configuredValue)
      ? configuredValue
      : configuredValue + offset;

const subtractOffset = (configuredValue: NumericValueConfig, offset: number) =>
  isReferencedValue(configuredValue) ? configuredValue : configuredValue - offset;

export const NumericConditionEditor = ({
  disabled = false,
  offset = 0,
  values = EmptyValues,
  min = 0,
  max,
  onChange,
  formatStringOverrides,
}: NumericConditionEditorProps) => {
  const { operator, value, value2 } = values;
  const initialValues: NumberConditionFormValues = {
    operator: operator ?? "",
    value: addOffset(value, offset, min ?? 0),
    value2: addOffset(value2, offset, min ?? 0),
  };

  const handleSubmit = (formValues: NumberConditionFormValues) =>
    onChange({
      operator: formValues.operator ? formValues.operator : undefined,
      value: subtractOffset(formValues.value, offset),
      value2: isTernaryOperator(formValues.operator) ? subtractOffset(formValues.value2, offset) : undefined,
    });

  return (
    <NumberConditionForm {...{ disabled, min, max, formatStringOverrides, initialValues, onSubmit: handleSubmit }} />
  );
};

type NumericConditionSummaryProps = Pick<
  NumericConditionEditorProps,
  "values" | "title" | "offset" | "formatStringOverrides"
>;

export const NumericConditionSummary = ({
  values = EmptyValues,
  title,
  offset = 0,
  formatStringOverrides,
}: NumericConditionSummaryProps) => {
  const mergedFormatStrings = useMemo(() => ({ ...formatStrings, ...formatStringOverrides }), [formatStringOverrides]);
  const formatMessage = useFormatMessage(mergedFormatStrings);
  const { value, value2, operator } = values;
  const first = value === undefined ? "" : <ValueConfigSummaryText value={value} offset={offset} />;
  const second = value2 === undefined ? "" : <ValueConfigSummaryText value={value2} offset={offset} />;

  return (
    <ConditionBuilderSummary
      title={title}
      operator={operatorToEnglish(operator)}
      value={
        <>
          {isTernaryOperator(operator) ? (
            <>
              {first} and {second}
            </>
          ) : (
            first
          )}{" "}
          {formatMessage("unitsSuffix")}
        </>
      }
    />
  );
};

const formatStrings = {
  unitsSuffix: {
    en: "",
  },
};
